import type { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import type {
  Action,
  ActionAction,
  AuthFeatureData,
  FooterDataSdk,
  HeaderSdk,
  Pages,
} from "~/core";
import type { DocumentItem } from "~/core/deprecated/documents";
import type {
  GetLayoutTranslationsQuery,
  GetSettingsQuery as GetSettingsQuerySdk,
} from "~/core/sdk";
import { isClickAction } from "~/core/utils/action.utils";
import { Footer, FooterTranslationProvider } from "~/features/footer";
import { FooterV3 } from "~/features/footerV3/FooterV3";
import { Header } from "~/features/header/Header";
import { HeaderV3 } from "~/features/headerV3/HeaderV3";
import { useUiKitComponents } from "~/shared/ui-kit";
import { makePlayNowUrl } from "~/shared/utils/aff-integration";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { useAuthModals } from "~/widgets/auth-modals";

export type MainProps = {
  lang: string;
  featureFlags: {
    isMenuConstructorEnabled: boolean;
    isFooterMenuConstructorEnabled: boolean;
    isCreateButtonEnabled: boolean;
    isRegistrationFeatureEnabled: boolean;
    isNavigationEnabled: boolean;
    isLanguageSelectorEnabled: boolean;
    isFooterNavigationLinkEnabled: boolean;
  };
  documents: Array<DocumentItem>;
  affiliate?: string;
  header?: HeaderSdk & { languages: GetSettingsQuerySdk["languages"] };
  authFeatures: AuthFeatureData;
  translations: GetLayoutTranslationsQuery;
  availableLanguages: Array<string>;
  isAuth: boolean;
  footer?: FooterDataSdk;
};

export const Main: FC<PropsWithChildren<MainProps>> = ({
  featureFlags,
  documents,
  affiliate,
  translations,
  header,
  isAuth,
  children,
  footer,
  availableLanguages,
  lang,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("footer");
  const { Link } = useUiKitComponents();
  const { gtm } = useGtmContext();
  const redirectToRoom = (): void => {
    const playNowLink = makePlayNowUrl(affiliate);
    if (playNowLink) {
      window.location.href = playNowLink;
    }
  };

  const headerAnalytics = {
    clickHeaderMain(triggerUrl: string) {
      const triggerId = triggerUrl.replace(/^\/+|\/+$/g, "");
      gtm.clickHeaderMain({
        eventTime: new Date(),
        playerID: null,
        triggerId: triggerId === "promotions" ? "Features" : triggerId,
      });
    },
    clickHeaderSub(targetUrl: string) {
      const targetId =
        targetUrl
          .replace(/^\/|\/$/g, "")
          .split("/")
          .at(-1) ?? "";

      gtm.clickHeaderSub({
        eventTime: new Date(),
        playerID: null,
        targetId,
        // одна из кнопок Хедера в котором кликнули на элемент Выпадающего меню (пока только Features). Аналитика требует название кнопки как на сайте
        triggerId: "Features",
        screenName: "mainPage",
      });
    },
    createAccountHeader() {
      gtm.createAccountHeader({
        eventTime: new Date(),
        playerID: null,
        screenName: "signUp",
      });
      redirectToRoom();
    },
    loginHeader() {
      gtm.loginHeader({
        eventTime: new Date(),
        playerID: null,
        screenName: "logIn",
      });
      redirectToRoom();
    },
  };

  const footerStaticTranslations = {
    info: {
      rights:
        translations.static_translations?.footer?.[0]?.widget_info_rights ??
        t("widget.info.rights"),
      gamblingAddictive:
        translations.static_translations?.footer?.[0]
          ?.widget_info_gamblingAddictive ??
        t("widget.info.gambling-addictive"),
      playResponsibly:
        translations.static_translations?.footer?.[0]
          ?.widget_info_playResponsibly ?? t("widget.info.play-responsibly"),
      supportDescription:
        translations.static_translations?.footer?.[0]
          ?.widget_info_supportDescription ??
        t("widget.info.support-description"),
      supportEmail:
        translations.static_translations?.footer?.[0]
          ?.widget_info_supportEmail ?? "widget.info.support-email",
    },
  };

  const { open } = useAuthModals();

  const getUrl = (detailedPage: Pages): string => {
    if (!detailedPage?.slug) {
      return `/`;
    }

    const { segment, slug } = detailedPage;
    if (segment) {
      return `/${segment}/${slug}`;
    } else {
      return `/${slug}`;
    }
  };

  const onCallAction = (action: ActionAction): void => {
    if (!isClickAction(action.item)) {
      return;
    }

    if (!window?.location) {
      return;
    }

    if (action.item.type === "signIn") {
      if (featureFlags.isRegistrationFeatureEnabled) {
        open("login");
        gtm.createAccountHeader({
          eventTime: new Date(),
          playerID: null,
          screenName: "logIn",
        });
      } else {
        headerAnalytics.loginHeader();
      }
    }

    if (action.item.type === "signUp") {
      if (featureFlags.isRegistrationFeatureEnabled) {
        open("register");
        gtm.createAccountHeader({
          eventTime: new Date(),
          playerID: null,
          screenName: "signUp",
        });
      } else {
        headerAnalytics.createAccountHeader();
      }
    }

    if (action.item.type === "signOut") {
      //
    }
  };

  const isActionInactive = (control: Action): boolean => {
    if (control.inactive_if?.includes("authorized") && isAuth) {
      return true;
    }
    if (control.inactive_if?.includes("unauthorized") && !isAuth) {
      return true;
    }

    return false;
  };

  return (
    <main className="min-h-screen flex flex-col justify-between">
      {featureFlags.isMenuConstructorEnabled && header ? (
        <HeaderV3
          {...headerAnalytics}
          data={{ ...header, language: lang, availableLanguages }}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isActionInactive={isActionInactive}
        />
      ) : (
        <Header
          playNowLink={makePlayNowUrl(affiliate)}
          featureFlags={featureFlags}
          analytics={headerAnalytics}
        />
      )}

      {children}
      {featureFlags.isFooterMenuConstructorEnabled && footer ? (
        <FooterV3
          data={footer}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
        />
      ) : (
        <FooterTranslationProvider value={footerStaticTranslations}>
          <Footer
            renderLink={(url, content) => <Link href={url}>{content}</Link>}
            featureFlags={featureFlags}
            documents={documents}
            language={language}
          />
        </FooterTranslationProvider>
      )}
    </main>
  );
};
